import { inject, injectable } from "tsyringe";
import { ViewModel } from "../../lib/vvm";
import { NAVIGATION_STORE_TOKEN, Screens } from "../../stores/navigation";
import type { INavigationStore } from "../../stores/navigation";
import { Product } from "../../stores/Product";
import { Box } from "../../stores/Box";
import { STORE_TOKEN } from "../../stores/store";
import type { IStore, SearchResultItem } from "../../stores/store";
import {
  WAREHOUSE_API_TOKEN,
  type IWarehouseApi,
} from "../../api/WarehouseApi";
import { formatTypeTag, normalizeTypeTag } from "../../utils/typeTags";
import { Measures } from "../../types/entities";

const PRODUCT_ID_PATTERN = /^prod_[a-zA-Z0-9-_]+$/;
const BOX_ID_PATTERN = /^box_[a-zA-Z0-9-_]+$/;

@injectable()
export class SearchResultsViewModel extends ViewModel {
  constructor(
    @inject(STORE_TOKEN) private readonly store: IStore,
    @inject(WAREHOUSE_API_TOKEN) private readonly api: IWarehouseApi,
    @inject(NAVIGATION_STORE_TOKEN)
    private readonly navigationStore: INavigationStore,
  ) {
    super();
  }

  get isSearchLoading(): boolean {
    return this.store.isSearchLoading;
  }

  get searchError(): string | null {
    return this.store.searchError;
  }

  get searchItems(): SearchResultItem[] {
    return this.store.searchItems;
  }

  openSearchItem = (searchItem: SearchResultItem): void => {
    if (searchItem.kind === "box") {
      this.openBox(searchItem.item);
      return;
    }
    this.openProduct(searchItem.item);
  };

  private openProduct = (product: Product): void => {
    this.navigationStore.setScreen(Screens.Product, product);
  };

  private openBox = (box: Box): void => {
    this.navigationStore.setScreen(Screens.Box, box);
  };

  openProductTag = (tag: string): void => {
    const normalizedTag = normalizeTypeTag(tag || "");
    if (!normalizedTag) {
      return;
    }

    this.navigationStore.setScreen(Screens.ProductsList, {
      title: `Товары по тегу ${formatTypeTag(normalizedTag)}`,
      typeTag: normalizedTag,
    });
  };

  openBoxTag = (tag: string): void => {
    const normalizedTag = normalizeTypeTag(tag || "");
    if (!normalizedTag) {
      return;
    }

    this.navigationStore.setScreen(Screens.BoxesList, {
      title: `Коробки по тегу ${formatTypeTag(normalizedTag)}`,
      typeTag: normalizedTag,
    });
  };

  openProductMeasure = (measure: Measures): void => {
    this.navigationStore.setScreen(Screens.ProductsList, {
      title:
        measure === Measures.weight
          ? "Товары по мере: вес"
          : measure === Measures.liters
            ? "Товары по мере: литры"
            : "Товары по мере: количество",
      measure,
    });
  };

  openProductLocation = (item: Product): void => {
    const parentId = item.parentId?.trim() || "";
    if (BOX_ID_PATTERN.test(parentId)) {
      void this.openBoxById(parentId);
      return;
    }

    this.navigationStore.setScreen(Screens.ProductsList, {
      title: "Товары без коробки",
      placement: "withoutBox",
    });
  };

  openBoxParent = (item: Box): void => {
    const parentId = item.parentId?.trim() || "";
    if (BOX_ID_PATTERN.test(parentId)) {
      void this.openBoxById(parentId);
      return;
    }

    this.navigationStore.setScreen(Screens.BoxesList, {
      title: "Список коробок",
    });
  };

  openReferenceId = (id: string): void => {
    const normalizedId = id.trim();
    if (!normalizedId) {
      return;
    }

    if (PRODUCT_ID_PATTERN.test(normalizedId)) {
      const existingProduct = this.store.products.find(
        (item) => item.id === normalizedId,
      );
      if (existingProduct) {
        this.navigationStore.setScreen(Screens.Product, existingProduct);
        return;
      }

      void this.openProductById(normalizedId);
      return;
    }

    if (BOX_ID_PATTERN.test(normalizedId)) {
      const existingBox = this.store.boxes.find(
        (item) => item.id === normalizedId,
      );
      if (existingBox) {
        this.navigationStore.setScreen(Screens.Box, existingBox);
        return;
      }

      void this.openBoxById(normalizedId);
    }
  };

  private async openProductById(productId: string): Promise<void> {
    try {
      const product = await this.api.getProductById(productId);
      if (!product) {
        return;
      }

      const model = new Product(product);
      this.store.addProduct(model);
      this.navigationStore.setScreen(Screens.Product, model);
    } catch {
      // Ошибка не критична: оставляем пользователя на текущем экране.
    }
  }

  private async openBoxById(boxId: string): Promise<void> {
    try {
      const box = await this.api.getBoxById(boxId);
      if (!box) {
        return;
      }

      const model = new Box(box);
      this.store.addBox(model);
      this.navigationStore.setScreen(Screens.Box, model);
    } catch {
      // Ошибка не критична: оставляем пользователя на текущем экране.
    }
  }
}
